import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, SettingsIcon, HelpCircleIcon, LogOutIcon, MessageSquareIcon, BellIcon, BuildingIcon, StoreIcon, CreditCardIcon, HeartIcon } from 'lucide-react';
import UserRoleToggle from '../../UI/UserRoleToggle';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { MobileMenuProps } from './types';
import { readerMenuSections, readerListsSections } from './readerMenuData';
import { vendorMenuSections } from './vendorMenuData';
import { useSwipeToClose } from './useSwipeToClose';
import { MenuSectionItem } from './MenuSectionItem';
const MobileMenu = forwardRef<HTMLDivElement, MobileMenuProps>(({
  isOpen,
  onClose,
  isReader,
  toggleUserRole
}, ref) => {
  const dk = useDarkClasses();
  const menuRef = useRef<HTMLDivElement>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]);
  };
  const isSectionExpanded = (sectionId: string) => {
    return expandedSections.includes(sectionId);
  };
  // Combine refs
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(menuRef.current);
      } else {
        ref.current = menuRef.current;
      }
    }
  }, [ref]);
  useSwipeToClose(menuRef, onClose);
  const renderMenuSection = (section: (typeof readerMenuSections)[number]) => <MenuSectionItem key={section.id} section={section} isExpanded={isSectionExpanded(section.id)} onToggle={toggleSection} onClose={onClose} isReader={isReader} darkMode={dk.isDark} data-id="element-2356" data-mp="2a0h31mqvx4i2" />;
  return <div className={`fixed inset-0 z-50 md:hidden ${isOpen ? 'visible' : 'invisible'}`} data-id="element-2357" data-mp="1vzedv14lfe2x">
        {/* Backdrop */}
        <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} data-id="element-2358" data-mp="1mbiwj50kpg31"></div>

        {/* Drawer */}
        <div ref={menuRef} className={`absolute top-0 left-0 bottom-0 w-[85%] max-w-sm shadow-2xl transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} ${dk.isDark ? 'bg-gray-900' : 'bg-white'}`} data-id="element-2359" data-mp="1cfz2yp9hethg">
          {/* User info section with safe area padding */}
          <div className={`p-6 pt-safe border-b ${isReader ? dk.isDark ? 'bg-indigo-900/30 border-gray-700' : 'bg-indigo-50' : dk.isDark ? 'bg-purple-900/30 border-gray-700' : 'bg-purple-50'}`} data-id="element-2360" data-mp="1vda1u8r7bkwd">
            <div className="flex items-center space-x-3 mt-4" data-id="element-2361" data-mp="2o0mm2c9w3gmi">
              <div className={`h-14 w-14 rounded-full flex items-center justify-center shadow-sm ${isReader ? dk.isDark ? 'bg-indigo-900/50' : 'bg-indigo-100' : dk.isDark ? 'bg-purple-900/50' : 'bg-purple-100'}`} data-id="element-2362" data-mp="2sse3nxb2ic0i">
                <UserIcon className={`h-7 w-7 ${isReader ? 'text-indigo-500' : 'text-purple-600'}`} data-id="element-2363" data-mp="cdj4z4x2stwt" />
              </div>
              <div data-id="element-2364" data-mp="m4dqg72hzph7">
                <div className={`font-bold text-lg ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-2365" data-mp="m3vkgiq19ww3">
                  Welcome!
                </div>
                <div className={`text-sm font-medium ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-2366" data-mp="3pl414z90yqp8">
                  {isReader ? 'Reader Account' : 'Vendor Account'}
                </div>
              </div>
            </div>
            <div className="mt-5" data-id="element-2367" data-mp="2i90maj7gafv">
              <UserRoleToggle isReader={isReader} toggleUserRole={toggleUserRole} data-id="element-2368" data-mp="1itv53bjgklv1" />
            </div>
          </div>

          {/* Menu items */}
          <div className="overflow-y-auto h-full pb-32 scrollbar-hide overscroll-contain" style={{
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-2369" data-mp="295q2gatcgn0g">
            {isReader ? <ReaderMenuContent renderMenuSection={renderMenuSection} onClose={onClose} darkMode={dk.isDark} data-id="element-2370" data-mp="2j2mf8sq9zvat" /> : <VendorMenuContent renderMenuSection={renderMenuSection} onClose={onClose} darkMode={dk.isDark} data-id="element-2371" data-mp="2s540p4y3wjwr" />}
          </div>
        </div>
      </div>;
});
MobileMenu.displayName = 'MobileMenu';
export default MobileMenu;
/* ---------- Sub-section renderers ---------- */
interface MenuContentProps {
  renderMenuSection: (section: any) => React.ReactNode;
  onClose: () => void;
  darkMode: boolean;
}
function ReaderMenuContent({
  renderMenuSection,
  onClose,
  darkMode
}: MenuContentProps) {
  return <div className="py-2" data-id="element-2372" data-mp="1472y4wkmr9xc">
      <div className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2373" data-mp="147cq2pp8jr5o">
        Discover
      </div>
      {readerMenuSections.map(renderMenuSection)}

      <div className={`px-4 py-3 text-xs font-bold uppercase tracking-wider mt-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2374" data-mp="v655tbjk0qul">
        Your Lists
      </div>
      {readerListsSections.map(renderMenuSection)}

      <div className={`px-4 py-3 text-xs font-bold uppercase tracking-wider mt-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2375" data-mp="1ivh9jvw446pi">
        Account
      </div>
      <Link to="/account-settings" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-indigo-50'}`} onClick={onClose} data-id="element-2376" data-mp="1e2o6dcgal6fj">
        <SettingsIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2377" data-mp="21uli73jqrygn" />
        <span className="text-base font-medium" data-id="element-2378" data-mp="3w1nqkwiwxupk">Account Settings</span>
      </Link>
      <Link to="/support" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-indigo-50'}`} onClick={onClose} data-id="element-2379" data-mp="3y4zfn8s0zmi">
        <HelpCircleIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2380" data-mp="3ranshny256lh" />
        <span className="text-base font-medium" data-id="element-2381" data-mp="3cdewui1q1gny">Help Center</span>
      </Link>
      <button className={`flex items-center w-full text-left px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-indigo-50'}`} onClick={onClose} data-id="element-2382" data-mp="3hdje4aageff8">
        <LogOutIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2383" data-mp="38de6q059fh12" />
        <span className="text-base font-medium" data-id="element-2384" data-mp="11mogwvvzec52">Sign Out</span>
      </button>
    </div>;
}
function VendorMenuContent({
  renderMenuSection,
  onClose,
  darkMode
}: MenuContentProps) {
  return <div className="py-2" data-id="element-2385" data-mp="1gpohjmcbiyzn">
      <div className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2386" data-mp="1gmske08aoec9">
        Manage
      </div>
      {vendorMenuSections.map(renderMenuSection)}

      <div className={`px-4 py-3 text-xs font-bold uppercase tracking-wider mt-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2387" data-mp="1kr0c86a1uaep">
        Account
      </div>
      <Link to="/vendor-profile" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2388" data-mp="1zhhchmay6ua0">
        <StoreIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2389" data-mp="24e7s68iubnuw" />
        <span className="text-base font-medium" data-id="element-2390" data-mp="e1jqzag8bxqn">Vendor Profile</span>
      </Link>
      <Link to="/messages" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2391" data-mp="a0zt24nu0q3g">
        <MessageSquareIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2392" data-mp="13jy8b7aredp3" />
        <span className="text-base font-medium" data-id="element-2393" data-mp="18bzq18qp4wmi">Messages</span>
      </Link>
      <Link to="/notifications" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2394" data-mp="1cdb90vf2ejlq">
        <BellIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2395" data-mp="kmf36erf9f7y" />
        <span className="text-base font-medium" data-id="element-2396" data-mp="om5tkhd1dtby">Notifications</span>
      </Link>
      <Link to="/account-settings" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2397" data-mp="to4vrnx1ik7b">
        <SettingsIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2398" data-mp="253jrkodij60u" />
        <span className="text-base font-medium" data-id="element-2399" data-mp="2a6knjt0jwmnu">Account Settings</span>
      </Link>
      <Link to="/store-settings" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2400" data-mp="2a8x0frrgc6u1">
        <BuildingIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2401" data-mp="1wfp7nwrmx5ar" />
        <span className="text-base font-medium" data-id="element-2402" data-mp="1rftc8lx06m6t">Store Settings</span>
      </Link>
      <Link to="/account/subscriptions" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2403" data-mp="1mggh7dvza8t5">
        <CreditCardIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2404" data-mp="3gtpwjmb7gojb" />
        <span className="text-base font-medium" data-id="element-2405" data-mp="3cu784gk6dvbq">Manage Subscriptions</span>
      </Link>
      <Link to="/vendors/favorites" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2406" data-mp="hnfkna78c0w0">
        <HeartIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2407" data-mp="rku0p8p8qnns" />
        <span className="text-base font-medium" data-id="element-2408" data-mp="3v3wuihd9i5ag">Favorites</span>
      </Link>
      <Link to="/support" className={`flex items-center px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2409" data-mp="3v1u6g2vo6m8d">
        <HelpCircleIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2410" data-mp="3xlyizn3zupz" />
        <span className="text-base font-medium" data-id="element-2411" data-mp="3ccvsfkguhtmo">Help Center</span>
      </Link>
      <button className={`flex items-center w-full text-left px-4 py-4 touch-target-lg ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-purple-50'}`} onClick={onClose} data-id="element-2412" data-mp="37ec6vl65yjuu">
        <LogOutIcon className={`h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2413" data-mp="1rnjs6qlwhciv" />
        <span className="text-base font-medium" data-id="element-2414" data-mp="1dwmzz0vvyxcc">Sign Out</span>
      </button>
    </div>;
}